import { PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import JSZip from "jszip";
import { COMPLETE_TAILWIND_CSS } from "@/assets/completeTailwindCSS";
import { convex } from "@/lib/convexClient";
import { UnifiedS3CredentialService } from "@/services/s3/UnifiedS3CredentialService";
import { StorageServiceFactory } from "@/services/storage/StorageServiceFactory";
import { StorageService } from "@/services/storageService";
import { getBunnyPullZoneHost } from "@/services/storage/BunnyStorageClient";
import { useEditorStore } from "@/store/editorStore";
import type { Tables } from "@/types/database";
import { api } from "../../convex/_generated/api";

type Project = Tables<"projects">;
type Panorama = Tables<"panoramas">;
type Hotspot = Tables<"hotspots">;

interface ExportData {
  project: Project;
  panoramas: Panorama[];
  hotspots: Hotspot[];
  skinComponents?: Array<Record<string, unknown>>;
}

const downloadFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export { downloadFile };

const getCleanFilenameFromUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  const rawName = url.split("/").pop();
  if (!rawName) return null;
  const [withoutQuery] = rawName.split("?");
  return withoutQuery || null;
};

// Helper to extract file path from S3 signed URL
const extractPathFromS3Url = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split("/");
    // Remove the first empty element and the bucket name
    return pathParts.slice(2).join("/");
  } catch {
    return null;
  }
};

// Helper to extract file path from Bunny CDN URL (pull zone)
const extractPathFromBunnyUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    const pullZoneHost = getBunnyPullZoneHost();
    const isBunnyHost =
      urlObj.host.endsWith(".b-cdn.net") || (pullZoneHost && urlObj.host === pullZoneHost);
    if (!isBunnyHost) return null;
    return urlObj.pathname.replace(/^\/+/, "");
  } catch {
    return null;
  }
};

// Helper to extract file path from cloud storage URL
const extractPathFromCloudStorageUrl = (url: string): string | null => {
  try {
    const signedMarker = "/storage/v1/object/sign/";
    if (url.includes(signedMarker)) {
      const rest = url.split(signedMarker)[1];
      return rest.split("?")[0].replace(/^staging\//, "");
    }

    const publicMarker = "/storage/v1/object/public/";
    if (url.includes(publicMarker)) {
      const rest = url.split(publicMarker)[1];
      return rest.split("?")[0].replace(/^staging\//, "");
    }

    return null;
  } catch {
    return null;
  }
};

// Helper to refresh a single URL based on its type (cloud storage or S3)
const refreshSingleUrl = async (url: string, craftspaceId: string): Promise<string | null> => {
  try {
    if (url.startsWith("app://storage/")) {
      return url;
    }
    const cloudPath = extractPathFromCloudStorageUrl(url);
    if (cloudPath) {
      const {
        data: { signedUrl },
      } = await StorageService.getPrivateUrl("staging", cloudPath);
      return signedUrl;
    } else if (url.startsWith("./") || url.startsWith("../") || !url.includes("://")) {
      // Relative URL - convert to proper file path and get signed URL
      const filePath = url.replace(/^\.\//, ""); // Remove leading ./

      // For S3, we need to construct the full path with craftspace and project info
      // The relative URL like "panoramas/window.jpg" needs to be converted to
      // "craftspaceId/projectId/panoramas/panoramaName/window.jpg"
      const store = useEditorStore.getState();
      const project = store.project;

      if (project?.craftspace_id) {
        // Try to construct the full S3 path
        const fullPath = `${project.craftspace_id}/${project.id}/${filePath}`;

        try {
          const storageService = await StorageServiceFactory.createStorageService(craftspaceId);
          const signedUrl = await storageService.getFileUrl(fullPath);
          return signedUrl;
        } catch (error) {
          console.warn(`[Export] Failed to get signed URL for full path ${fullPath}:`, error);

          // Fallback: try the original file path
          try {
            const storageService = await StorageServiceFactory.createStorageService(craftspaceId);
            const signedUrl = await storageService.getFileUrl(filePath);
            return signedUrl;
          } catch (fallbackError) {
            console.error(`[Export] Both full path and fallback failed:`, fallbackError);
            return null;
          }
        }
      } else {
        console.warn(`[Export] No project or craftspace info available for relative URL conversion`);
        return null;
      }
    } else {
      const bunnyPath = extractPathFromBunnyUrl(url);
      if (bunnyPath) {
        const storageService = await StorageServiceFactory.createStorageService(craftspaceId);
        const newSignedUrl = await storageService.getFileUrl(bunnyPath);
        return newSignedUrl;
      }
      // S3 URL
      const filePath = extractPathFromS3Url(url);
      if (filePath) {
        const storageService = await StorageServiceFactory.createStorageService(craftspaceId);
        const newSignedUrl = await storageService.getFileUrl(filePath);
        return newSignedUrl;
      }
    }
    return null;
  } catch (error) {
    console.error(`[Export] Error refreshing URL:`, error);
    return null;
  }
};

// Helper function to determine content type based on file extension
const getContentType = (filename: string): string => {
  const ext = filename.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "html":
    case "htm":
      return "text/html";
    case "css":
      return "text/css";
    case "js":
      return "text/javascript";
    case "json":
      return "application/json";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "svg":
      return "image/svg+xml";
    case "ico":
      return "image/x-icon";
    default:
      return "application/octet-stream";
  }
};

export const exportProject = async (
  project: Project,
  panoramas: Panorama[],
  hotspots: Hotspot[],
) => {
  let skinComponents: Array<Record<string, unknown>> = [];
  if (project.data && typeof project.data === "object" && "skinComponents" in project.data) {
    const rawSkinComponents = (project.data as Record<string, unknown>).skinComponents;
    if (Array.isArray(rawSkinComponents)) {
      skinComponents = rawSkinComponents as Array<Record<string, unknown>>;
    }
  }

  const exportData: ExportData = {
    project,
    panoramas,
    hotspots,
    skinComponents,
  };

  const blob = new Blob([JSON.stringify(exportData, null, 2)], {
    type: "application/json",
  });
  const filename = `${project.name.replace(/[^a-zA-Z0-9]/g, "_")}_project_data.json`;
  downloadFile(blob, filename);
};

const getExportRootName = (projectName?: string) => {
  const base = (projectName || "360-tour").trim() || "360-tour";
  return base.replace(/[\\/]/g, "-");
};

const getBunnyConfig = () => {
  const storageZone = import.meta.env.VITE_BUNNY_STORAGE_ZONE as string | undefined;
  const accessKey = import.meta.env.VITE_BUNNY_ACCESS_KEY as string | undefined;
  const storageHost =
    (import.meta.env.VITE_BUNNY_STORAGE_HOST as string | undefined) || "storage.bunnycdn.com";
  const pullZoneUrl = import.meta.env.VITE_BUNNY_PULL_ZONE_URL as string | undefined;
  const publicPullZoneUrl = import.meta.env.VITE_BUNNY_PUBLIC_PULL_ZONE_URL as string | undefined;
  const publishStorageZone = import.meta.env.VITE_BUNNY_PUBLISH_STORAGE_ZONE as string | undefined;
  const publishAccessKey = import.meta.env.VITE_BUNNY_PUBLISH_ACCESS_KEY as string | undefined;
  const publishStorageHost =
    (import.meta.env.VITE_BUNNY_PUBLISH_STORAGE_HOST as string | undefined) || storageHost;

  return {
    storageZone,
    accessKey,
    storageHost,
    pullZoneUrl,
    publicPullZoneUrl,
    publishStorageZone,
    publishAccessKey,
    publishStorageHost,
  };
};

const joinUrl = (base: string, path: string) =>
  `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;

// Helper function to load the React viewer bundle for exports
const loadReactViewerBundle = async (): Promise<string> => {
  try {
    // Try to fetch the React viewer bundle from the public directory
    const response = await fetch("/viewer-export.js");

    if (response.ok) {
      const contentType = response.headers.get("content-type")?.toLowerCase() || "";
      const bundleContent = await response.text();
      const trimmed = bundleContent.trimStart();
      const trimmedLower = trimmed.slice(0, 100).toLowerCase();
      const looksLikeHtml =
        contentType.includes("text/html") ||
        contentType.includes("application/xhtml+xml") ||
        trimmedLower.startsWith("<!doctype") ||
        trimmedLower.startsWith("<html") ||
        trimmedLower.startsWith("<head") ||
        trimmedLower.startsWith("<body") ||
        trimmedLower.startsWith("<!--");

      if (looksLikeHtml) {
        throw new Error(
          "viewer-export.js returned HTML. Run `npm run build:viewer` so public/viewer-export.js exists.",
        );
      }
      console.log(
        "📦 React viewer bundle loaded successfully, size:",
        bundleContent.length,
        "characters",
      );
      return bundleContent;
    } else {
      console.warn("⚠️ React viewer bundle not found, status:", response.status);
      return createSimpleViewer();
    }
  } catch (error) {
    console.error("❌ Error loading React viewer bundle:", error);
    return createSimpleViewer();
  }
};

// Fallback simple viewer (should never be used - indicates build error)
const createSimpleViewer = () => {
  return `
// ERROR: React viewer bundle not found!
// This indicates a build or deployment issue.

const waitForTourData = () => {
  if (window.TOUR_DATA) {
    initializeViewer();
  } else {
    setTimeout(waitForTourData, 100);
  }
};

const initializeViewer = () => {
  const root = document.getElementById('root');
  if (!root) {
    console.error('❌ Root element not found');
    return;
  }
  
  while (root.firstChild) {
    root.removeChild(root.firstChild);
  }

  const container = document.createElement("div");
  container.style.cssText =
    "display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: Arial, sans-serif; text-align: center; padding: 20px;";

  const title = document.createElement("h2");
  title.style.cssText = "color: #e74c3c; margin-bottom: 20px;";
  title.textContent = "🚨 Build Error";

  const message = document.createElement("p");
  message.style.cssText = "color: #666; margin-bottom: 10px;";
  message.textContent = "React viewer bundle not found.";

  const rebuild = document.createElement("p");
  rebuild.style.cssText = "color: #666; margin-bottom: 20px;";
  rebuild.textContent = "Please rebuild the project with: npm run build";

  const details = document.createElement("div");
  details.style.cssText =
    "background: #f8f9fa; padding: 15px; border-radius: 5px; font-family: monospace; font-size: 12px; color: #333; white-space: pre-line;";
  details.textContent =
    "Expected: public/viewer-export.js\nThis is a fallback viewer and should not be used in production.";

  container.appendChild(title);
  container.appendChild(message);
  container.appendChild(rebuild);
  container.appendChild(details);
  root.appendChild(container);
  
  console.error('🚨 CRITICAL: React viewer bundle not available - using fallback viewer');
  console.error('🚨 This indicates a build or deployment issue');
};

waitForTourData();
`;
};

export const exportWebTour = async (
  project: Project,
  panoramas: Panorama[],
  hotspots: Hotspot[],
  options: { download?: boolean; prepareForS3?: boolean; adminPasswordHash?: string } = {
    download: true,
  },
): Promise<Blob | { files: Map<string, Blob>; html: string }> => {
  // Load CMS data from backend (SQLite or Convex)
  let cmsData: Record<string, unknown> | null = null;
  try {
    const { CMSService } = await import("@/services/cmsService");
    cmsData = await CMSService.exportCMSDataToJSON(project.id);
  } catch (error) {
    console.warn("Failed to load CMS data for export:", error);
    // Continue without CMS data
  }

  // Hash admin password if provided
  let adminPasswordHash: string | undefined = options.adminPasswordHash;
  if (options.adminPasswordHash && !options.adminPasswordHash.startsWith("$")) {
    // If it's a plain password, hash it
    try {
      const { AdminAuthService } = await import("@/utils/adminAuthService");
      adminPasswordHash = await AdminAuthService.hashPassword(options.adminPasswordHash);
    } catch (error) {
      console.warn("Failed to hash admin password:", error);
      adminPasswordHash = undefined;
    }
  }
  const exportId = Math.random().toString(36).substr(2, 9);

  const zip = new JSZip();
  const exportRootName = getExportRootName(project.name);
  const rootFolder = zip.folder(exportRootName) || zip;

  // Store successfully downloaded panoramas to avoid re-downloading for S3
  const downloadedPanoramas = new Map<string, Blob>();

  // Store mapping of panorama IDs to their local file URLs for HTML generation
  const panoramaLocalUrls = new Map<string, { fileUrl: string; privateFileUrl: string; thumbnailUrl?: string; thumbnailUrlWebp?: string }>();

  // Helper function to get a signed URL for a panorama with multiple fallbacks
  const getSignedUrl = async (panorama: Panorama): Promise<string | null> => {
    try {
      // console.log(`[Export] Getting signed URL for panorama: ${panorama.name}`);
      // console.log(`[Export] Available URLs:`, {
      //   file_url: panorama.file_url,
      //   private_file_url: panorama.private_file_url
      // });

      // Get craftspace ID for refreshing URLs
      const store = useEditorStore.getState();
      const craftspaceId = store.project?.craftspace_id || "default";

      // Try multiple sources in order of preference
      const urlSources = [
        { name: "private_file_url", url: panorama.private_file_url },
        { name: "file_url", url: panorama.file_url },
      ].filter((source) => source.url); // Filter out null/undefined URLs

      for (const source of urlSources) {
        const src = source.url;

        if (src) {
          // Try to refresh the URL (works for both cloud-managed storage and S3)
          try {
            const refreshedUrl = await refreshSingleUrl(src, craftspaceId);
            if (refreshedUrl) {
              return refreshedUrl;
            }
          } catch (urlError) {
            console.warn(
              `[Export] Failed to refresh URL for ${panorama.name} from ${source.name}:`,
              urlError,
            );
          }

          // If refresh failed, only use absolute URLs as fallback (skip relative URLs)
          if (src && (src.startsWith("http://") || src.startsWith("https://"))) {
            try {
              const testResponse = await fetch(src, {
                method: "GET",
                headers: { Range: "bytes=0-1" },
              });
              if (testResponse.ok) {
                console.log(`[Export] Using original URL from ${source.name} as fallback`);
                return src;
              } else {
                console.warn(
                  `[Export] Original URL from ${source.name} is invalid (${testResponse.status}), skipping`,
                );
              }
            } catch (testError) {
              console.warn(
                `[Export] Original URL from ${source.name} failed validation:`,
                testError,
              );
            }
          } else {
            console.warn(`[Export] Skipping relative URL from ${source.name}: ${src}`);
          }
        }
      }

      console.error(`[Export] No valid URLs found for panorama ${panorama.name}`);
      return null;
    } catch (error) {
      console.error(`[Export] Error getting signed URL for panorama ${panorama.id}:`, error);
      return null;
    }
  };

  // Create scripts folder
  const scriptsFolder = rootFolder.folder("scripts");

  // Add panorama files
  const panoramasFolder = rootFolder.folder("panoramas");
  if (panoramasFolder) {
    // console.log(`[Export] Processing ${panoramas.length} panoramas...`);
    // console.log(`[Export] Panorama data:`, panoramas.map(p => ({
    //   id: p.id,
    //   name: p.name,
    //   file_url: p.file_url,
    //   private_file_url: p.private_file_url
    // })));

    for (const panorama of panoramas) {
      try {
        // console.log(`[Export] Processing panorama ${panorama.id}: ${panorama.name}`);
        // console.log(`[Export] Original URLs:`, {
        //   file_url: panorama.file_url,
        //   private_file_url: panorama.private_file_url
        // });

        const signedUrl = await getSignedUrl(panorama);
        // console.log(`[Export] Signed URL for ${panorama.name}:`, signedUrl);

        if (!signedUrl) {
          console.warn(`[Export] Could not get signed URL for panorama ${panorama.id}`);
          continue;
        }

        // Download the panorama with better error handling
        const response = await fetch(signedUrl);

        if (!response.ok) {
          console.warn(
            `[Export] Failed to download panorama ${panorama.id}: ${response.status} ${response.statusText}`,
          );
          continue;
        }

        const panoramaBlob = await response.blob();

        // Validate that we got a proper image file
        if (panoramaBlob.size < 10000) {
          // Less than 10KB is suspicious for a panorama
          console.error(
            `[Export] Panorama blob is too small: ${panoramaBlob.size} bytes - likely corrupted or error response`,
          );

          // Try to read the blob as text to see if it's an error message
          const blobText = await panoramaBlob.text();
          console.error(`[Export] Small blob content:`, blobText);
          continue;
        }

        // Handle case where file_url might be null - use panorama name for filename
        let filename: string;
        if (panorama.file_url) {
          filename = getCleanFilenameFromUrl(panorama.file_url) || `panorama_${panorama.id}.jpg`;
        } else if (panorama.name) {
          // Use panorama name with proper extension
          filename = `${panorama.name}.jpg`;
        } else {
          filename = `panorama_${panorama.id}.jpg`;
        }

        // Create local variables for the exported tour (don't modify original panorama object)
        const localFileUrl = `./panoramas/${filename}`;
        const localPrivateFileUrl = `./panoramas/${filename}`;

        // Download thumbnails and give them local paths too
        let localThumbnailUrl: string | undefined;
        let localThumbnailUrlWebp: string | undefined;
        const thumbUrlWebp = panorama.thumbnail_url_webp;
        const thumbUrl = panorama.thumbnail_url;
        const exportCraftspaceId = useEditorStore.getState().project?.craftspace_id || "default";
        for (const [thumbSrc, isWebp] of [[thumbUrlWebp, true], [thumbUrl, false]] as [string | undefined | null, boolean][]) {
          if (!thumbSrc) continue;
          try {
            const refreshed = await refreshSingleUrl(thumbSrc, exportCraftspaceId);
            const fetchUrl = refreshed || (thumbSrc.startsWith("http") ? thumbSrc : null);
            if (!fetchUrl) continue;
            const thumbResp = await fetch(fetchUrl);
            if (!thumbResp.ok) continue;
            const thumbBlob = await thumbResp.blob();
            if (thumbBlob.size < 100) continue;
            const srcFilename = thumbSrc.split("/").pop()?.split("?")[0] ?? "";
            const srcExt = srcFilename.includes(".") ? srcFilename.split(".").pop() : (isWebp ? "webp" : "jpg");
            const thumbFilename = `thumbnail_${panorama.id}${isWebp ? "_webp" : ""}.${srcExt}`;
            const localThumbPath = `./panoramas/${thumbFilename}`;
            panoramasFolder.file(thumbFilename, thumbBlob);
            downloadedPanoramas.set(thumbFilename, thumbBlob);
            if (isWebp) {
              localThumbnailUrlWebp = localThumbPath;
            } else {
              localThumbnailUrl = localThumbPath;
            }
          } catch {
            // Thumbnail download failure is non-fatal
          }
        }

        // Store the local URLs for this panorama
        panoramaLocalUrls.set(panorama.id, {
          fileUrl: localFileUrl,
          privateFileUrl: localPrivateFileUrl,
          thumbnailUrl: localThumbnailUrl,
          thumbnailUrlWebp: localThumbnailUrlWebp,
        });

        panoramasFolder.file(filename, panoramaBlob);

        // Store the downloaded panorama for reuse in S3 upload
        downloadedPanoramas.set(filename, panoramaBlob);

        // console.log(`[Export] ✅ Successfully added panorama: ${filename} (${panoramaBlob.size} bytes)`);
      } catch (error) {
        console.error(`[Export] Error processing panorama ${panorama.id}:`, error);
        // Even if download fails, create local variables to prevent null errors
        const localFileUrl = `./panoramas/panorama_${panorama.id}.jpg`;
        const localPrivateFileUrl = `./panoramas/panorama_${panorama.id}.jpg`;

        // Store the local URLs for this panorama
        panoramaLocalUrls.set(panorama.id, {
          fileUrl: localFileUrl,
          privateFileUrl: localPrivateFileUrl,
        });
      }
    }
  }

  // Add the React viewer bundle
  if (scriptsFolder) {
    try {
      // Load the React viewer bundle
      const viewerContent = await loadReactViewerBundle();
      const viewerBlob = new Blob([viewerContent], { type: "application/javascript" });
      const viewerFilename = "viewer-export.js";
      scriptsFolder.file(viewerFilename, viewerBlob);

      // Store the loader filename for HTML reference
      (scriptsFolder as { _loaderFilename?: string })._loaderFilename = viewerFilename;

      // Add IndexedDB CMS service if CMS data exists
      if (cmsData && cmsData.length > 0) {
        // The IndexedDB CMS service is inlined in the exported HTML when CMS data exists.
      }
    } catch (error) {
      console.error("[Export] CRITICAL: Error creating viewer for export:", error);
      throw new Error("Export failed: Could not create viewer");
    }
  }

  // Create the HTML content with the correct loader filename
  const loaderFilename =
    (scriptsFolder as { _loaderFilename?: string })?._loaderFilename || "viewer-export.js";
  // Create panoramas with local URLs for HTML generation
  const panoramasForHTML = panoramas.map((panorama) => {
    const localUrls = panoramaLocalUrls.get(panorama.id);
    if (localUrls) {
      return {
        ...panorama,
        file_url: localUrls.fileUrl,
        private_file_url: localUrls.privateFileUrl,
        ...(localUrls.thumbnailUrl !== undefined && { thumbnail_url: localUrls.thumbnailUrl }),
        ...(localUrls.thumbnailUrlWebp !== undefined && { thumbnail_url_webp: localUrls.thumbnailUrlWebp }),
      };
    }
    return panorama;
  });

  const htmlContent = createHTMLContent(
    project,
    panoramasForHTML,
    hotspots,
    loaderFilename,
    cmsData,
    adminPasswordHash,
  );
  rootFolder.file("index.html", htmlContent);

  // Create the ZIP file
  const zipBlob = await zip.generateAsync({ type: "blob" });

  if (options.download) {
    // Download the file
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${project.name || "360-tour"}_${exportId}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return zipBlob;
  } else {
    // Return the files for S3 upload
    const files = new Map<string, Blob>();

    // Add the HTML file (critical!)
    files.set("index.html", new Blob([htmlContent], { type: "text/html" }));
    // console.log('[Export] Added index.html');

    // Add the viewer-export.js file for S3 upload
    const viewerContent = await loadReactViewerBundle();
    const viewerBlob = new Blob([viewerContent], { type: "application/javascript" });
    files.set("scripts/viewer-export.js", viewerBlob);
    // console.log('[Export] Added viewer.js');

    // Add all panorama files (reuse the already downloaded panoramas)
    // console.log(`[Export S3] Available downloaded panoramas:`, Array.from(downloadedPanoramas.keys()));
    for (const [filename, panoramaBlob] of downloadedPanoramas.entries()) {
      const s3Filename = `panoramas/${filename}`;
      files.set(s3Filename, panoramaBlob);
      // console.log(`[Export S3] ✅ Reused panorama: ${s3Filename} (${panoramaBlob.size} bytes)`);
    }

    // If no panoramas were downloaded in the main section, try to download them now
    if (downloadedPanoramas.size === 0) {
      console.log(
        "[Export S3] No panoramas found in downloaded cache, attempting fresh download...",
      );
      for (const panorama of panoramas) {
        try {
          const signedUrl = await getSignedUrl(panorama);
          if (signedUrl) {
            console.log(`[Export S3] Attempting to fetch: ${signedUrl}`);
            const response = await fetch(signedUrl);
            console.log(`[Export S3] Response status: ${response.status} ${response.statusText}`);

            if (response.ok) {
              const panoramaBlob = await response.blob();
              console.log(`[Export S3] Downloaded blob size: ${panoramaBlob.size} bytes`);

              // Validate that we got a proper image file
              if (panoramaBlob.size < 10000) {
                console.error(
                  `[Export S3] Panorama blob is too small: ${panoramaBlob.size} bytes - skipping`,
                );
                const blobText = await panoramaBlob.text();
                console.error(`[Export S3] Small blob content:`, blobText);
                continue;
              }

              // Handle case where file_url might be null - use panorama name for filename
              let baseFilename: string;
              if (panorama.file_url) {
                baseFilename =
                  getCleanFilenameFromUrl(panorama.file_url) || `panorama_${panorama.id}.jpg`;
              } else if (panorama.name) {
                baseFilename = `${panorama.name}.jpg`;
              } else {
                baseFilename = `panorama_${panorama.id}.jpg`;
              }

              const filename = `panoramas/${baseFilename}`;
              files.set(filename, panoramaBlob);
              console.log(
                `[Export S3] ✅ Successfully added panorama: ${filename} (${panoramaBlob.size} bytes)`,
              );
            } else {
              console.warn(
                `[Export S3] Failed to download panorama ${panorama.id}: ${response.status} ${response.statusText}`,
              );
            }
          }
        } catch (error) {
          console.error(`[Export S3] Error processing panorama ${panorama.id}:`, error);
        }
      }
    }

    // Add React bundle files (use actual built files)
    let loaderFilename = "viewer.js";
    try {
      // Updated viewer content that works with current bundle exports
      const workingViewerContent = `
console.log('[Export] Starting viewer initialization...');

// Override WebGL renderer settings for exported tours
window.EXPORT_WEBGL_CONFIG = {
  powerPreference: "default", // Use default instead of high-performance
  antialias: true, // Enable antialiasing to prevent hotspot flickering
  failIfMajorPerformanceCaveat: true, // Fail if performance is too poor
  preserveDrawingBuffer: false,
  alpha: false,
  depth: true,
  stencil: false
};

try {
  const bundle = await import("./index-CE_qujeT.js");
  console.log('[Export] Bundle loaded:', Object.keys(bundle));
  const n = bundle.aS; // React DOM createRoot (createRoot as aS)
  const j = bundle.j; // JSX factory (jsxRuntimeExports as j)
  const r = bundle.r; // React hooks (reactExports as r)
  const a = bundle.au; // Viewer360 component (Viewer360 as au)
  const k = bundle.aP; // AuthProvider (AuthProvider as aP)
  console.log('[Export] React components loaded successfully', { n: !!n, j: !!j, r: !!r, a: !!a, k: !!k });
  
  const e = window.TOUR_DATA;
  console.log('[Export] Tour data:', e);
  console.log('[Export] WebGL support:', {
    webgl: !!window.WebGLRenderingContext,
    webgl2: !!window.WebGL2RenderingContext,
    devicePixelRatio: window.devicePixelRatio
  });
  
  const p = () => {
    r.useEffect(() => {
      var o;
      if (e) {
        console.log('[Export] Setting editor state with tour data');
        // Note: Store state will be handled by the Viewer360 component itself
        console.log('[Export] Tour data available, component will handle state internally');
      }
    }, []);
    
    if (!e) {
      console.log('[Export] No tour data available');
      return j.jsx("div", { children: "Tour data is missing." });
    }
    
    console.log('[Export] Rendering Viewer360 component with conservative WebGL settings');
    return j.jsx(a, {
      project: e.project,
      panoramas: e.panoramas,
      hotspots: e.hotspots,
      skinComponents: e.skinComponents || [],
      isPreviewMode: true
    });
  };
  
  console.log('[Export] Creating React root and rendering...');
  
  // Create a mock auth context for export
  const mockAuthContext = {
    user: { id: 'export-user', email: 'export@example.com' },
    userProfile: null,
    claims: null,
    session: null,
    loading: false,
    signIn: () => Promise.resolve({ error: null }),
    signUp: () => Promise.resolve({ error: null }),
    signOut: () => Promise.resolve({ error: null }),
    resetPassword: () => Promise.resolve({ error: null }),
    updatePassword: () => Promise.resolve({ error: null }),
    updateEmail: () => Promise.resolve({ error: null }),
    updateProfile: () => Promise.resolve({ error: null }),
    refreshSession: () => Promise.resolve({ error: null }),
    getSession: () => Promise.resolve({ data: { session: null }, error: null }),
    onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } })
  };
  
  n.createRoot(document.getElementById("root")).render(
    j.jsx(r.StrictMode, { 
      children: j.jsx(k, { 
        children: j.jsx(a, {}),
        value: mockAuthContext
      }) 
    })
  );
  console.log('[Export] React app rendered successfully');
  
} catch (error) {
  console.error('[Export] Failed to load or initialize viewer:', error);
  const root = document.getElementById("root");
  if (root) {
    while (root.firstChild) {
      root.removeChild(root.firstChild);
    }
    const errorContainer = document.createElement("div");
    errorContainer.style.cssText = "color: white; padding: 20px; background: #1a1a2e;";
    const safeMessage =
      error && error.message ? String(error.message) : "Unknown error";
    errorContainer.textContent = "Error loading viewer: " + safeMessage;
    root.appendChild(errorContainer);
  }
}`;

      // Use the latest built bundle with sphere rotation fixes
      const workingMainResponse = await fetch("/assets/index-DscG3UvD.js");
      if (!workingMainResponse.ok) {
        throw new Error(
          `Could not fetch current React bundle: ${workingMainResponse.status} ${workingMainResponse.statusText}`,
        );
      }

      // Verify it's actually JavaScript content
      const contentType = workingMainResponse.headers.get("content-type") || "";
      if (!contentType.includes("javascript") && !contentType.includes("text/plain")) {
        console.warn(`[Export S3] Warning: Expected JavaScript, got content-type: ${contentType}`);
      }
      const mainBlob = await workingMainResponse.blob();
      const mainFilename = "index-DscG3UvD.js"; // Use latest bundle with sphere rotation fixes
      loaderFilename = "viewer-BnY4pmaS.js";
      // console.log(`[Export S3] Using current React bundle with working filename`);

      // Create viewer blob from the updated content
      const viewerBlob = new Blob([workingViewerContent], { type: "application/javascript" });

      files.set(`scripts/${mainFilename}`, mainBlob);
      files.set(`scripts/${loaderFilename}`, viewerBlob);

      // console.log(`[Export S3] Added current React bundle (${mainFilename})`);
      // console.log(`[Export S3] Added updated viewer (${loaderFilename}) - compatible with current bundle`);
      // console.log(`[Export S3] Viewer content preview:`, workingViewerContent.substring(0, 200) + '...');
    } catch (fetchError) {
      console.warn("[Export] Could not fetch built assets, using fallback viewer:", fetchError);
      const fallbackViewer = await loadReactViewerBundle();
      const viewerBlob = new Blob([fallbackViewer], { type: "application/javascript" });
      files.set("scripts/viewer-export.js", viewerBlob);
      console.log("[Export] Added React viewer (fallback)");
      loaderFilename = "viewer-export.js";
    }

    // Update the HTML content with the correct loader filename
    // Create panoramas with local URLs for HTML generation
    const panoramasForHTML = panoramas.map((panorama) => {
      const localUrls = panoramaLocalUrls.get(panorama.id);
      if (localUrls) {
        return {
          ...panorama,
          file_url: localUrls.fileUrl,
          private_file_url: localUrls.privateFileUrl,
          ...(localUrls.thumbnailUrl !== undefined && { thumbnail_url: localUrls.thumbnailUrl }),
          ...(localUrls.thumbnailUrlWebp !== undefined && { thumbnail_url_webp: localUrls.thumbnailUrlWebp }),
        };
      }
      return panorama;
    });

    const updatedHtmlContent = createHTMLContent(
      project,
      panoramasForHTML,
      hotspots,
      loaderFilename,
      cmsData,
      adminPasswordHash,
    );
    return { files, html: updatedHtmlContent };
  }
};

// Helper function to get the complete Tailwind CSS from the working tour
const getCompleteTailwindCSS = (): string => {
  try {
    // Use the imported complete CSS constant
    // console.log(`[Export] ✅ Using complete Tailwind CSS (${COMPLETE_TAILWIND_CSS.length} chars) from imported constant`);
    return COMPLETE_TAILWIND_CSS;
  } catch (error) {
    console.warn("[Export] Could not load complete CSS from imported constant:", error);
  }

  console.log("[Export] ⚠️ Using fallback minimal CSS - exported tour may not render correctly");

  // Fallback to minimal CSS if we can't read the complete version
  return `<style>
  *,
  :before,
  :after {
    --tw-border-spacing-x: 0;
    --tw-border-spacing-y: 0;
    --tw-translate-x: 0;
    --tw-translate-y: 0;
    --tw-rotate: 0;
    --tw-skew-x: 0;
    --tw-skew-y: 0;
    --tw-scale-x: 1;
    --tw-scale-y: 1;
    --tw-pan-x: ;
    --tw-pan-y: ;
    --tw-pinch-zoom: ;
    --tw-scroll-snap-strictness: proximity;
    --tw-gradient-from-position: ;
    --tw-gradient-via-position: ;
    --tw-gradient-to-position: ;
    --tw-ordinal: ;
    --tw-slashed-zero: ;
    --tw-numeric-figure: ;
    --tw-numeric-spacing: ;
    --tw-numeric-fraction: ;
    --tw-ring-inset: ;
    --tw-ring-offset-width: 0px;
    --tw-ring-offset-color: #fff;
    --tw-ring-color: rgb(59 130 246 / 0.5);
    --tw-ring-offset-shadow: 0 0 #0000;
    --tw-ring-shadow: 0 0 #0000;
    --tw-shadow: 0 0 #0000;
    --tw-shadow-colored: 0 0 #0000;
    --tw-blur: ;
    --tw-brightness: ;
    --tw-contrast: ;
    --tw-grayscale: ;
    --tw-hue-rotate: ;
    --tw-invert: ;
    --tw-saturate: ;
    --tw-sepia: ;
    --tw-drop-shadow: ;
    --tw-backdrop-blur: ;
    --tw-backdrop-brightness: ;
    --tw-backdrop-contrast: ;
    --tw-backdrop-grayscale: ;
    --tw-backdrop-hue-rotate: ;
    --tw-backdrop-invert: ;
    --tw-backdrop-opacity: ;
    --tw-backdrop-saturate: ;
    --tw-backdrop-sepia: ;
    --tw-contain-size: ;
    --tw-contain-layout: ;
    --tw-contain-paint: ;
    --tw-contain-style: ;
  }
  ::backdrop {
    --tw-border-spacing-x: 0;
    --tw-border-spacing-y: 0;
    --tw-translate-x: 0;
    --tw-translate-y: 0;
    --tw-rotate: 0;
    --tw-skew-x: 0;
    --tw-skew-y: 0;
    --tw-scale-x: 1;
    --tw-scale-y: 1;
    --tw-pan-x: ;
    --tw-pan-y: ;
    --tw-pinch-zoom: ;
    --tw-scroll-snap-strictness: proximity;
    --tw-gradient-from-position: ;
    --tw-gradient-via-position: ;
    --tw-gradient-to-position: ;
    --tw-ordinal: ;
    --tw-slashed-zero: ;
    --tw-numeric-figure: ;
    --tw-numeric-spacing: ;
    --tw-numeric-fraction: ;
    --tw-ring-inset: ;
    --tw-ring-offset-width: 0px;
    --tw-ring-offset-color: #fff;
    --tw-ring-color: rgb(59 130 246 / 0.5);
    --tw-ring-offset-shadow: 0 0 #0000;
    --tw-ring-shadow: 0 0 #0000;
    --tw-shadow: 0 0 #0000;
    --tw-shadow-colored: 0 0 #0000;
    --tw-blur: ;
    --tw-brightness: ;
    --tw-contrast: ;
    --tw-grayscale: ;
    --tw-hue-rotate: ;
    --tw-invert: ;
    --tw-saturate: ;
    --tw-sepia: ;
    --tw-drop-shadow: ;
    --tw-backdrop-blur: ;
    --tw-backdrop-brightness: ;
    --tw-backdrop-contrast: ;
    --tw-backdrop-grayscale: ;
    --tw-backdrop-hue-rotate: ;
    --tw-backdrop-invert: ;
    --tw-backdrop-opacity: ;
    --tw-backdrop-saturate: ;
    --tw-backdrop-sepia: ;
    --tw-contain-size: ;
    --tw-contain-layout: ;
    --tw-contain-paint: ;
    --tw-contain-style: ;
  }
  *,
  ::before,
  ::after {
    box-sizing: border-box;
    border-width: 0;
    border-style: solid;
    border-color: #e5e7eb;
  }
  ::before,
  ::after {
    --tw-content: "";
  }
  html,
  :host {
    line-height: 1.5;
    -webkit-text-size-adjust: 100%;
    -moz-tab-size: 4;
    tab-size: 4;
    font-family:
      ui-sans-serif,
      system-ui,
      sans-serif,
      "Apple Color Emoji",
      "Segoe UI Emoji",
      "Segoe UI Symbol",
      "Noto Color Emoji";
    font-feature-settings: normal;
    font-variation-settings: normal;
    -webkit-tap-highlight-color: transparent;
  }
  body {
    margin: 0;
    line-height: inherit;
  }
  #root {
    width: 100vw;
    height: 100vh;
    overflow: hidden;
  }
</style>`;
};

// Helper function to create the HTML content that matches 0tour exactly
const createHTMLContent = (
  project: Project,
  panoramas: Panorama[],
  hotspots: Hotspot[],
  loaderFilename: string = "viewer-loader.js",
  cmsData: Record<string, unknown> | null = null,
  adminPasswordHash?: string,
) => {
  const tourData = {
    project,
    panoramas,
    hotspots,
    skinComponents: project.data?.skinComponents || [],
  };

  // Get the complete Tailwind CSS
  const completeCss = getCompleteTailwindCSS();

  // Create IndexedDB initialization script if CMS data exists
  const indexedDBInitScript =
    cmsData && cmsData.length > 0
      ? `
    <script>
      // Initialize IndexedDB with CMS data before viewer loads (using native IndexedDB API)
      (async function() {
        try {
          const projectId = '${project.id}';
          const dbName = \`360crafter_cms_\${projectId}\`;
          const DB_VERSION = 1;
          
          // Open IndexedDB using native API
          const request = indexedDB.open(dbName, DB_VERSION);
          
          const db = await new Promise((resolve, reject) => {
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
            request.onupgradeneeded = (event) => {
              const db = event.target.result;
              if (!db.objectStoreNames.contains('collections')) {
                const collectionStore = db.createObjectStore('collections', { keyPath: 'id' });
                collectionStore.createIndex('by-project', 'id');
              }
              if (!db.objectStoreNames.contains('records')) {
                const recordStore = db.createObjectStore('records', { keyPath: 'id' });
                recordStore.createIndex('by-collection', 'collectionId');
                recordStore.createIndex('by-project', 'id');
              }
            };
          });
          
          // Clear existing data
          await new Promise((resolve, reject) => {
            const tx = db.transaction(['collections', 'records'], 'readwrite');
            tx.objectStore('collections').clear();
            tx.objectStore('records').clear();
            tx.oncomplete = resolve;
            tx.onerror = reject;
          });
          
          const cmsData = ${JSON.stringify(cmsData)};
          
          // Transform to IndexedDB format
          const collections = cmsData.map(collection => ({
            id: collection.id,
            name: collection.name,
            description: collection.description,
            fieldStructure: collection.fieldStructure,
            createdAt: collection.metadata?.createdAt || new Date().toISOString(),
            updatedAt: collection.metadata?.updatedAt || new Date().toISOString(),
          }));
          
          const records = [];
          cmsData.forEach(collection => {
            collection.rows.forEach((row, index) => {
              records.push({
                id: \`\${collection.id}_\${index}\`,
                collectionId: collection.id,
                data: row,
                createdAt: collection.metadata?.createdAt || new Date().toISOString(),
                updatedAt: collection.metadata?.updatedAt || new Date().toISOString(),
              });
            });
          });
          
          // Import collections
          await new Promise((resolve, reject) => {
            const tx = db.transaction('collections', 'readwrite');
            collections.forEach(collection => {
              tx.objectStore('collections').put(collection);
            });
            tx.oncomplete = resolve;
            tx.onerror = reject;
          });
          
          // Import records
          await new Promise((resolve, reject) => {
            const tx = db.transaction('records', 'readwrite');
            records.forEach(record => {
              tx.objectStore('records').put(record);
            });
            tx.oncomplete = resolve;
            tx.onerror = reject;
          });
          
          console.log('[Export] CMS data initialized in IndexedDB:', { collections: collections.length, records: records.length });
        } catch (error) {
          console.error('[Export] Failed to initialize CMS data in IndexedDB:', error);
        }
      })();
    </script>
  `
      : "";

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>360 Tour Viewer</title>
    <script type="module" src="./scripts/${loaderFilename}"></script>
    
    ${completeCss}
    <link
      rel="icon"
      href="data:image/svg+xml, %3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27%3E%3Ccircle cx=%278%22 cy=%278%22 r=%278%27 fill=%27%230D6CFF%27/%3E%3Ctext x=%278%27 y=%2711%27 font-size=%2710%27 text-anchor=%27middle%27 fill=%27white%27%3Ei%3C/text%3E%3C/svg%3E" />
  </head>
  <body>
    <script>
      window.TOUR_DATA = ${JSON.stringify(tourData, null, 8)};
      ${cmsData && cmsData.length > 0 ? `window.CMS_DATA = ${JSON.stringify(cmsData, null, 8)};` : ""}
      ${adminPasswordHash ? `window.ADMIN_CONFIG = { passwordHash: ${JSON.stringify(adminPasswordHash)} };` : ""}
      console.log(
        "[Export] TOUR_DATA injected",
        window.TOUR_DATA ? Object.keys(window.TOUR_DATA) : "missing"
      );
      ${cmsData && cmsData.length > 0 ? 'console.log("[Export] CMS_DATA injected", window.CMS_DATA ? window.CMS_DATA.length + " collections" : "missing");' : ""}
      ${adminPasswordHash ? 'console.log("[Export] Admin mode enabled");' : ""}
    </script>
    ${indexedDBInitScript}
    <div id="root"></div>
  </body>
</html>`;
};

export const publishWebTour = async (
  project: Project,
  panoramas: Panorama[],
  hotspots: Hotspot[],
  _user: { id: string },
  _password?: string,
  adminPassword?: string,
  options?: { productionSlug?: string },
): Promise<{ url: string; bucketUrl: string; exportBlob?: Blob }> => {
  try {
    // Hash admin password if provided
    let adminPasswordHash: string | undefined;
    if (adminPassword) {
      try {
        const { AdminAuthService } = await import("@/utils/adminAuthService");
        adminPasswordHash = await AdminAuthService.hashPassword(adminPassword);
      } catch (error) {
        console.warn("Failed to hash admin password:", error);
      }
    }

    // First, create the export package with individual files for S3
    const exportResult = await exportWebTour(project, panoramas, hotspots, {
      download: false,
      prepareForS3: true,
      adminPasswordHash,
    });

    // Type guard to ensure we have the files
    if (!("files" in exportResult)) {
      throw new Error("Export failed to prepare files for S3");
    }

    const { files } = exportResult;

    // Get the current user's S3 connection
    const store = useEditorStore.getState();
    const currentCraftspace = store.project?.craftspace_id;

    if (!currentCraftspace) {
      throw new Error("No craftspace found for publishing");
    }

    let craftspace: Record<string, unknown> | null = null;
    try {
      craftspace = await convex.query(api.craftspaces.get, { id: currentCraftspace });
    } catch (error) {
      console.warn("Failed to read craftspace storage_provider, defaulting to 360crafter:", error);
    }

    const provider =
      (craftspace?.storage_provider as "360crafter" | "custom-s3" | null) || "360crafter";

    // Branch by provider
    if (provider === "custom-s3") {
      // Use S3 storage for published tours
      // console.log('🔍 DEBUG - Using S3 storage for published tours');

      const s3Connection = await convex.query(api.s3Connections.getByCraftspace, {
        craftspaceId: currentCraftspace,
      });

      if (!s3Connection) {
        throw new Error(
          "S3 connection required for published tours. Please configure S3 storage in craftspace settings.",
        );
      }

      // Load S3 credentials from unified storage (handles both encrypted and unencrypted)
      const creds = await UnifiedS3CredentialService.getCredentials(currentCraftspace);
      if (!creds) {
        throw new Error(
          "No S3 credentials found. Please configure S3 storage in craftspace settings.",
        );
      }

      // Import S3 client and commands (already imported at top)

      // Build S3 client similar to ClientS3Manager
      const normalizeEndpoint = (endpoint: string): string => {
        let e = endpoint.trim();
        if (e.endsWith("/")) e = e.slice(0, -1);
        return e;
      };
      const shouldUsePathStyle = (endpoint: string): boolean => {
        return !endpoint.includes("amazonaws.com") && !endpoint.includes("digitaloceanspaces.com");
      };

      // Use S3 for publishing instead of cloud-managed storage
      const endpoint = normalizeEndpoint(s3Connection.endpoint || creds.endpoint);
      const s3Client = new S3Client({
        endpoint,
        region: s3Connection.region || creds.region,
        credentials: {
          accessKeyId: creds.accessKeyId,
          secretAccessKey: creds.secretAccessKey,
        },
        forcePathStyle: shouldUsePathStyle(endpoint),
      });

      // Use the existing bucket for exports (MinIO might not allow creating new buckets)
      const exportsBucketName = s3Connection.bucket_name;
      // console.log('Using existing bucket for exports:', exportsBucketName);

      // Use custom slug if available, otherwise generate from project name
      let projectSlug: string;
      if (project.data && typeof project.data === "object" && project.data.customSlug) {
        projectSlug = project.data.customSlug;
      } else {
        projectSlug = project.name.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase();
      }
      const craftspaceSlug =
        typeof craftspace?.slug === "string" && craftspace.slug.trim().length > 0
          ? craftspace.slug
          : "published";
      const folderName = `${craftspaceSlug}/${projectSlug}`;

      // console.log(`[Export] Publishing to folder: ${folderName}`);
      // console.log(`[Export] Using bucket: ${exportsBucketName}`);
      // console.log(`[Export] Using endpoint: ${s3Connection.endpoint}`);

      // CUSTOM-S3 PROVIDER - Fix AWS SDK streaming issues
      const uploadFile = async (key: string, blob: Blob) => {
        const fullKey = `${folderName}/${key}`;
        const contentType = getContentType(key);
        // console.log(`[Export] Uploading file: ${fullKey} (${blob.size} bytes) ContentType: ${contentType}`);

        // Convert Blob to Uint8Array to avoid AWS SDK streaming issues (browser compatible)
        const arrayBuffer = await blob.arrayBuffer();
        await s3Client.send(
          new PutObjectCommand({
            Bucket: exportsBucketName,
            Key: fullKey,
            Body: new Uint8Array(arrayBuffer),
            ContentType: contentType,
            // Make publicly readable
            ACL: "public-read",
          }),
        );

        // console.log(`[Export] Successfully uploaded: ${fullKey}`);
      };

      // PUBLISH DEBUG - Enhanced logging for CUSTOM-S3 provider
      // console.log(`[Export] PUBLISH DEBUG - Files to upload:`, Array.from(files.keys()));
      const _panoramaFiles = Array.from(files.keys()).filter((path) =>
        path.startsWith("panoramas/"),
      );
      // console.log(`[Export] PUBLISH DEBUG - Panorama files found:`, panoramaFiles);

      // Upload all individual files directly
      // console.log(`[Export] Uploading ${files.size} files to S3...`);
      for (const [relativePath, blob] of files.entries()) {
        // console.log(`[Export] Uploading: ${relativePath}`);
        await uploadFile(relativePath, blob);
      }

      // Generate URLs
      const tourUrl = `${endpoint}/${exportsBucketName}/${folderName}/`;
      const bucketUrl = `${endpoint}/${exportsBucketName}/${folderName}/`;

      // console.log('Tour published successfully to S3!');
      // console.log('Tour URL:', tourUrl);
      // console.log('Bucket URL:', bucketUrl);

      try {
        await convex.mutation(api.publishedTours.create, {
          project_id: project.id,
          craftspace_id: currentCraftspace,
          bucket_name: exportsBucketName,
          folder_name: folderName,
          tour_url: tourUrl,
          bucket_url: bucketUrl,
          published_at: new Date().toISOString(),
        });
      } catch (dbError) {
        console.error("Failed to store published tour info:", dbError);
      }

      // Create export blob for download
      const exportZip = new JSZip();
      const exportRoot = exportZip.folder(getExportRootName(project.name)) || exportZip;
      for (const [relativePath, blob] of files.entries()) {
        exportRoot.file(relativePath, blob);
      }
      const exportBlob = await exportZip.generateAsync({ type: "blob" });

      return { url: tourUrl, bucketUrl: bucketUrl, exportBlob };
    } else {
      // 360crafter provider - publish to Bunny.net storage
      console.log("🔍 DEBUG - Using 360crafter provider, publishing to Bunny.net storage");

      const bunnyConfig = getBunnyConfig();
      const publishPullZoneUrl = bunnyConfig.publicPullZoneUrl || bunnyConfig.pullZoneUrl;
      const uploadStorageZone = bunnyConfig.publishStorageZone;
      const uploadAccessKey = bunnyConfig.publishAccessKey;
      const uploadStorageHost = bunnyConfig.publishStorageHost || bunnyConfig.storageHost;
      if (!uploadStorageZone || !uploadAccessKey || !publishPullZoneUrl) {
        throw new Error(
          "Bunny.net publishing is not configured. Set VITE_BUNNY_PUBLISH_STORAGE_ZONE + VITE_BUNNY_PUBLISH_ACCESS_KEY and VITE_BUNNY_PUBLIC_PULL_ZONE_URL (or VITE_BUNNY_PULL_ZONE_URL).",
        );
      }

      // Use custom slug if available, otherwise generate from project name
      let projectSlug: string;
      if (project.data && typeof project.data === "object" && project.data.customSlug) {
        projectSlug = project.data.customSlug;
      } else {
        projectSlug = project.name.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase();
      }
      const craftspaceSlug =
        typeof craftspace?.slug === "string" && craftspace.slug.trim().length > 0
          ? craftspace.slug
          : "published";
      const folderName = `${craftspaceSlug}/${projectSlug}`;
      let customDomainForPublish: string | null = null;
      let customDomainId: string | null = null;
      let customDomainFolder: string | null = null;

      try {
        const selectedDomainId =
          typeof (project as { custom_domain_id?: string }).custom_domain_id === "string"
            ? (project as { custom_domain_id: string }).custom_domain_id
            : null;
        if (selectedDomainId) {
          const selected = await convex.query(api.customDomains.get, {
            id: selectedDomainId as any,
          });
          if (selected?.domain && selected.status === "verified") {
            customDomainForPublish = selected.domain
              .replace("https://", "")
              .replace("http://", "");
            customDomainId = selectedDomainId;
            customDomainFolder = `${craftspaceSlug}-${projectSlug}`;
            const internalProjectId =
              typeof (project as { _id?: string })._id === "string"
                ? (project as { _id: string })._id
                : (project.id as unknown as string);
            await convex.action(api.customDomains.ensureEdgeRuleForDomain, {
              domainId: selectedDomainId as any,
              projectId: internalProjectId as any,
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load custom domains for publish URL:", error);
      }

      console.log(
        `[Export] Publishing to Bunny.net folder: ${folderName || "(root)"}${
          customDomainForPublish ? ` for ${customDomainForPublish}` : ""
        }`,
      );

      // Upload files to Bunny.net storage
      const uploadTargets: string[] = [folderName];
      if (customDomainFolder) uploadTargets.push(customDomainFolder);
      if (options?.productionSlug) uploadTargets.push(options.productionSlug);

      for (const [relativePath, blob] of files.entries()) {
        for (const target of uploadTargets) {
          const filePath = target ? `${target}/${relativePath}` : relativePath;
          const contentType = getContentType(relativePath);
          const uploadUrl = joinUrl(
            `https://${uploadStorageHost}`,
            `${uploadStorageZone}/${filePath}`,
          );

          console.log(
            `[Export] Uploading to Bunny.net: ${filePath} (${blob.size} bytes) ContentType: ${contentType}`,
          );

          const response = await fetch(uploadUrl, {
            method: "PUT",
            headers: {
              AccessKey: uploadAccessKey,
              "Content-Type": contentType,
            },
            body: blob,
          });

          if (!response.ok) {
            const errorText = await response.text().catch(() => "");
            console.error(`[Export] Failed to upload ${filePath}:`, response.status, errorText);
            throw new Error(`Failed to upload ${relativePath}: ${response.status}`);
          }
        }
      }

      try {
        const purgeTargets = Array.from(files.keys()).filter(
          (path) => path === "index.html" || path.startsWith("scripts/"),
        );
        if (purgeTargets.length > 0) {
          const base = publishPullZoneUrl.replace(/\/+$/, "");
          const folderPrefix = folderName ? `${folderName.replace(/\/+$/, "")}/` : "";
          const urls = purgeTargets.map((path) => `${base}/${folderPrefix}${path}`);
          if (customDomainForPublish) {
            const customBase = `https://${customDomainForPublish.replace(/\/+$/, "")}`;
            urls.push(
              ...purgeTargets.map((path) => `${customBase}/${path.replace(/^\/+/, "")}`),
            );
          }
          await convex.action(api.customDomains.purgePullZoneCache, { urls });
        }
      } catch (error) {
        console.warn("Failed to purge Bunny cache:", error);
      }

      // Get the public URL for the tour
      const bunnyBaseUrl = joinUrl(publishPullZoneUrl, "/");
      let tourUrl = joinUrl(bunnyBaseUrl, `${folderName}/`);
      const bucketUrl = joinUrl(bunnyBaseUrl, `${folderName}/`);

      if (customDomainForPublish) {
        tourUrl = `https://${customDomainForPublish}/`;
      }

      try {
        await convex.mutation(api.publishedTours.create, {
          project_id: project.id,
          craftspace_id: currentCraftspace,
          bucket_name: "exports",
          folder_name: folderName,
          tour_url: tourUrl,
          bucket_url: bucketUrl,
          published_at: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Failed to record published tour:", error);
      }

      console.log("Tour published successfully to Bunny.net storage!");
      console.log("Tour URL:", tourUrl);
      console.log("Bucket URL:", bucketUrl);

      // Create export blob for download
      const exportZip = new JSZip();
      const exportRoot = exportZip.folder(getExportRootName(project.name)) || exportZip;
      for (const [relativePath, blob] of files.entries()) {
        exportRoot.file(relativePath, blob);
      }
      const exportBlob = await exportZip.generateAsync({ type: "blob" });

      return { url: tourUrl, bucketUrl: bucketUrl, exportBlob };
    }
  } catch (error) {
    console.error("Publish failed:", error);
    throw error;
  }
};
